/**
 * @fileoverview The sheet a gardener's own to-do is added and edited in (D50,
 * batch 6 of the to-do rebuild; Fable passes 1 and 2, the mock-up's 02a to
 * 03 and 07a). One box, three chips, one button:
 *
 * - The title: "A to-do for your garden" from Today, "A to-do for Cherokee
 *   Purple tomato" from a plant's card or a bed's (wrapping when long); "Edit
 *   this to-do" to edit.
 * - The box: one line, 17px and 48px tall (text-base h-12: iOS zooms into a
 *   box under 16px), focused as the sheet opens (BottomPanel initialFocus),
 *   its language the app's so the keyboard's microphone dictates in it,
 *   sentence capitals, Enter saves (enterKeyHint done; the form's submit, so
 *   Add under the keyboard never blocks a save), cut at the server's limit
 *   (limits.OWN_TODO_TITLE_MAX) with what is left counted near it.
 * - Straight under the box (adding and editing): the job pills (B140, JobChips) the server sends for what the
 *   to-do is about (brief.todo.jobLists: plant, bed or garden; none drawn when the list is empty). A tap rings the job and puts
 *   its word in the box: it replaces an empty box or another job's word alone, else it is added to the end with a space (cut at the
 *   title limit); the box stays focused. Tapping the ringed pill again unrings it and clears the job, the words stay. Add sends
 *   `job` only when one is ringed; Save sends `job` (a code, or null) only when a pill was touched. Picking another thing in For
 *   unrings a job that is not on its list. After them, the letters left, near the limit.
 * - When: Today (picked), This week, Pick a day (WhenChips: one row that never
 *   wraps, E25); the third shows "Which day?" and a date box, from the
 *   garden's today on.
 * - For (Today only): "Your garden ›", which opens the For picker.
 * - Nothing about plants (E25, owner, 30 Oct): "Plant something" is its own
 *   button and sheet beside "Add a to-do" (AddAndPlantButtons,
 *   PlantSomethingSheet); the old "A to-do / Plant something" switch is gone.
 * - The foot (rule 7): Cancel on the left, the same as the X, and Add on the
 *   right, disabled until the box has a letter (and a day, for Pick a day);
 *   Save in edit, with "Delete this to-do" far left as ActionRow's quiet button (B111), which asks in place
 *   (E25, rule 7): "Delete "Move the hose"?" with Cancel on the left and
 *   Delete on the right, filled red; Delete's toast keeps its words (no Undo
 *   once removed, B45).
 *
 * Edit shows the when the server says is planned (a to-do whose day has
 * passed shows Today) and sends the when only when a chip was touched, so
 * Save leaves its dates alone otherwise; nothing changed, it just closes. A
 * to-do carried from an earlier day shows its card's own pill words under
 * the chips ("Since Oct 21", the server's; E25), so the sheet and the card
 * agree.
 *
 * The app decides nothing about the to-do: the server places it, words it and
 * says the toast (useOwnTodos, useTodoMarks). A title typed and not saved
 * stays in the box until the page is left.
 *
 * A page draws it through useOwnTodoSheet, which keeps its state.
 *
 * Where: useOwnTodoSheet (TodayPage, OwnTodosRow).
 * API: through useOwnTodos (POST /todo/own, PATCH /todo/own/:id) and
 * useTodoMarks (Delete: the mark remove).
 * Settings: limits.OWN_TODO_TITLE_MAX (lib/growSettings);
 * OWN_TODO_COUNTER_WITHIN (here, the app's own layout value).
 * Words (en/es): todo.own.* (jobs, jobChosen, title, titleGarden, editTitle, placeholder,
 * when, whichDay, for, forGarden, addButton, save, delete, deleteConfirm,
 * deleteYes, left; today, week, day in WhenChips), sitewide.cancel
 * (ActionRow), sitewide.saving (Button's loading word while it saves).
 * Feet (B111): ActionRow and Button everywhere, 44px; `loading` while the server answers (Adding... for a new to-do, Saving... for an edit).
 * While the Delete question is open only its Cancel and Delete row shows; the form's Cancel and Save row is hidden (walk fix, 1 Nov 2026).
 * Delete this to-do waits (ActionRow removeLoading, the Deleting word) while the server removes the to-do.
 *
 * @module own-todos/OwnTodoSheet
 */

import React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { FailureText } from '@/shared/components/ServerFailure';
import type { OwnAbout, OwnWhen } from '@/shared/api/ownTodo';
import { useTodayBrief } from '@/shared/hooks/useTodayBrief';
import type { JobListItem } from '@/shared/api/todayBrief';
import type { TodoMarks } from '@/features/today/lib/useTodoMarks';
import type { OwnTodos } from './useOwnTodos';
import { ForPicker, type ForChoice } from './ForPicker';
import { JobChips } from './JobChips';
import { joinShortcut } from '@/shared/lib/shortcutText';
import { WhenChips } from './WhenChips';

/** What a new to-do is about when the sheet opens: the garden (Today, with the For row), or a plant or bed by name. */
export interface OwnAddTarget {
  about: OwnAbout;
  /** The plant or bed; null for the garden. */
  id: number | null;
  /** Its name for the sheet's title; '' for the garden. */
  name: string;
}

/** The to-do being edited. */
export interface OwnEditTarget {
  /** own_todo_id. */
  id: number;
  /** Its key, for Delete (the mark remove). */
  key: string;
  title: string;
  /** What the to-do is about: the job pills are that list's. */
  about: OwnAbout;
  /** The to-do's job (a code on the shared Jobs list), ringed on opening; null or a code no longer on the list rings nothing. */
  job?: string | null;
  /** The when the server says to show picked, and its day for Pick a day. */
  planned: { when: OwnWhen; day: string | null };
  /** A to-do carried from an earlier day: its card's pill words ("Since Oct 21", the server's), shown under the chips; null otherwise. */
  since?: string | null;
}

/** The sheet's mode: adding, or editing one. */
export type Mode = { kind: 'add'; target: OwnAddTarget } | { kind: 'edit'; target: OwnEditTarget };

/**
 * How close to the server's title limit the box starts counting down what is left, so the count reads as a warning
 * rather than decoration (NoteBox's rule, for a short title). Characters.
 * @kept K5
 */
const OWN_TODO_COUNTER_WITHIN = 20;

/** The sheet's props. */
export interface OwnTodoSheetProps {
  mode: Mode | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The garden's day, "YYYY-MM-DD" (the brief's): the first day Pick a day offers. */
  today: string;
  /** The writes (useOwnTodos). */
  own: OwnTodos;
  marks: TodoMarks;
  /** The box, focused as the sheet opens. */
  boxRef: React.RefObject<HTMLInputElement>;
}

/**
 * The add or edit sheet.
 *
 * @param props - OwnTodoSheetProps.
 */
export const OwnTodoSheet: React.FC<OwnTodoSheetProps> = ({ mode, open, onOpenChange, today, own, marks, boxRef }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const limits = useGrowLimits();
  const max = limits.OWN_TODO_TITLE_MAX;
  const [title, setTitle] = React.useState('');
  const [when, setWhen] = React.useState<OwnWhen>('today');
  const [day, setDay] = React.useState('');
  /** A chip was tapped (edit sends the when only then). */
  const [touched, setTouched] = React.useState(false);
  const [forChoice, setForChoice] = React.useState<ForChoice>({ about: 'garden', id: null, name: '' });
  const [picking, setPicking] = React.useState(false);
  /** The ringed job's code, or null. */
  const [job, setJob] = React.useState<string | null>(null);
  /** A job pill was tapped (edit sends `job` only then). */
  const [jobTouched, setJobTouched] = React.useState(false);
  const { brief } = useTodayBrief();
  /** The mode the fields were last filled for, so a new opening fills them afresh. */
  const [filledFor, setFilledFor] = React.useState<Mode | null>(null);
  /** "Delete this to-do" was tapped: the sheet asks in place, Cancel or Delete (E25, rule 7). */
  const [confirming, setConfirming] = React.useState(false);
  if (open && mode && mode !== filledFor) {
    setFilledFor(mode);
    setTouched(false);
    setJobTouched(false);
    setConfirming(false);
    if (mode.kind === 'edit') {
      setTitle(mode.target.title);
      setJob(mode.target.job ?? null);
      setWhen(mode.target.planned.when);
      setDay(mode.target.planned.day ?? '');
    } else if (filledFor?.kind === 'edit' || (filledFor?.kind === 'add' && filledFor.target.id !== mode.target.id)) {
      // A new to-do for another thing: a clean box (one typed for the same thing and not saved stays).
      setTitle('');
      setWhen('today');
      setDay('');
      setJob(null);
    }
    if (mode.kind === 'add' && mode.target.about !== 'garden') setForChoice({ about: mode.target.about, id: mode.target.id, name: mode.target.name });
    else if (mode.kind === 'add' && filledFor?.kind !== 'add') setForChoice({ about: 'garden', id: null, name: '' });
  }
  const editing = mode?.kind === 'edit';
  /** From Today: the For row shows. */
  const fromToday = mode?.kind === 'add' && mode.target.about === 'garden';
  const deleteKey = mode?.kind === 'edit' ? mode.target.key : null;
  const deleting = deleteKey !== null && marks.busyKey === deleteKey;
  const deleteFailed = deleteKey !== null && marks.failed?.key === deleteKey ? marks.failed.words : null;
  /** The jobs the server lists for what the to-do is about (a lookup by its key, not a rule). */
  const jobs: JobListItem[] = brief?.todo.jobLists?.[mode?.kind === 'edit' ? mode.target.about : forChoice.about] ?? [];
  const nearLimit = max !== undefined && title.length >= max - OWN_TODO_COUNTER_WITHIN;
  const ready = title.trim().length > 0 && (when !== 'day' || day >= today) && !own.busy && !deleting;

  /**
   * A tap on a job pill: the ringed one is unringed and the job cleared (the words stay); another is ringed and its word goes
   * in the box, replacing an empty box or another job's word alone, else added to the end (cut at the title limit).
   *
   * @param picked - The job tapped.
   */
  const pickJob = (picked: JobListItem): void => {
    setJobTouched(true);
    if (picked.code === job) {
      setJob(null);
    } else {
      setJob(picked.code);
      setTitle((current) => (current.trim() === '' || jobs.some((other) => other.label === current.trim())
        ? joinShortcut('', picked.label, max)
        : joinShortcut(current, picked.label, max)));
    }
    boxRef.current?.focus();
  };

  /**
   * Picks what a new to-do is for (Today's For row); a ringed job that is not on that list is unringed, the words stay.
   *
   * @param choice - The plant, bed or garden picked.
   */
  const pickFor = (choice: ForChoice): void => {
    setForChoice(choice);
    setPicking(false);
    const next = brief?.todo.jobLists?.[choice.about] ?? [];
    if (job && !next.some((other) => other.code === job)) setJob(null);
  };

  /**
   * Picks a When chip.
   *
   * @param next - The chip.
   */
  const pickWhen = (next: OwnWhen): void => {
    setWhen(next);
    setTouched(true);
  };

  /**
   * Saves: adds the to-do, or sends what changed; closes once it saved.
   *
   * @param event - The form's submit (Add, Save, or Enter in the box).
   */
  const save = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!ready || !mode) return;
    const words = title.trim();
    const dated = when === 'day' ? { day } : {};
    let saved = false;
    if (mode.kind === 'add') {
      const about = forChoice.about;
      saved = await own.add({
        title: words,
        when,
        ...dated,
        ...(job && jobs.some((other) => other.code === job) ? { job } : {}),
        about,
        ...(about === 'plant' && forChoice.id ? { plantObjId: forChoice.id } : {}),
        ...(about === 'bed' && forChoice.id ? { locationId: forChoice.id } : {}),
      });
      if (saved) {
        setTitle('');
        setWhen('today');
        setDay('');
        setJob(null);
        // The next one from Today starts at "Your garden" again (the default needs no tap).
        if (fromToday) setForChoice({ about: 'garden', id: null, name: '' });
      }
    } else {
      const change = {
        ...(words !== mode.target.title ? { title: words } : {}),
        ...(touched ? { when, ...dated } : {}),
        ...(jobTouched ? { job } : {}),
      };
      saved = Object.keys(change).length === 0 ? true : await own.edit(mode.target.id, change);
    }
    if (saved) onOpenChange(false);
  };

  /** Deletes the to-do being edited (its toast has no Undo, B45); closes once it saved. */
  const remove = async (): Promise<void> => {
    if (!deleteKey) return;
    if (await marks.mark(null, { action: 'remove', key: deleteKey })) onOpenChange(false);
  };

  const heading = editing
    ? t('todo.own.editTitle')
    : mode?.kind === 'add' && mode.target.about !== 'garden' ? t('todo.own.title', { name: mode.target.name }) : t('todo.own.titleGarden');
  const since = mode?.kind === 'edit' ? mode.target.since ?? null : null;

  return (
    <>
      <BottomPanel open={open} onOpenChange={onOpenChange} title={heading} wrapTitle initialFocus={boxRef}>
        <form onSubmit={(event) => { void save(event); }} className="space-y-4">
          <div>
            <input
              ref={boxRef}
              type="text"
              value={title}
              onChange={(event) => setTitle(max !== undefined ? event.target.value.slice(0, max) : event.target.value)}
              maxLength={max}
              lang={language}
              autoCapitalize="sentences"
              autoComplete="off"
              enterKeyHint="done"
              placeholder={t('todo.own.placeholder')}
              aria-label={heading}
              className="h-12 w-full rounded-md border-2 border-input bg-card px-3 text-base text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
            />
            <JobChips jobs={jobs} selected={job} onPick={pickJob} disabled={own.busy} className="mt-2" />
            {nearLimit && (
              <p className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
                <span className={cn('ml-auto shrink-0 tabular-nums', max !== undefined && title.length >= max ? 'text-destructive' : 'text-muted-foreground')} aria-live="polite">
                  {t('todo.own.left', { count: (max ?? 0) - title.length })}
                </span>
              </p>
            )}
          </div>

          <div>
            <p id="own-todo-when" className="mb-2 text-sm font-semibold text-foreground">{t('todo.own.when')}</p>
            <WhenChips value={when} onChange={pickWhen} labelledBy="own-todo-when" />
            {since && <p className="mt-2 text-sm text-muted-foreground">{since}</p>}
            {when === 'day' && (
              <label className="mt-3 block">
                <span className="mb-1 block text-sm font-semibold text-foreground">{t('todo.own.whichDay')}</span>
                <input
                  type="date"
                  min={today}
                  value={day}
                  onChange={(event) => { setDay(event.target.value); setTouched(true); }}
                  className="h-12 w-full rounded-md border border-input bg-card px-3 text-base text-foreground focus:border-primary focus:outline-none"
                />
              </label>
            )}
          </div>

          {fromToday && (
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="flex min-h-12 w-full items-center gap-2 rounded-md border border-border bg-card px-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="font-semibold text-foreground">{t('todo.own.for')}</span>
              <span className="ml-auto min-w-0 truncate text-muted-foreground">{forChoice.name || t('todo.own.forGarden')}</span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            </button>
          )}

          <div className="space-y-2">
            {!confirming && (
              <ActionRow
                onCancel={() => onOpenChange(false)}
                disabled={own.busy}
                onRemove={editing ? () => setConfirming(true) : undefined}
                removeLabel={t('todo.own.delete')}
                removeLoading={deleting}
                removeBusy="deleting"
              >
                <Button type="submit" disabled={!ready} loading={own.busy} busy={editing ? 'saving' : 'adding'}>
                  {editing ? t('todo.own.save') : t('todo.own.addButton')}
                </Button>
              </ActionRow>
            )}
            {own.failed && <p className="text-sm text-destructive" role="alert"><FailureText text={own.failed} /></p>}
            {/* Rule 7: Delete asks in place, Cancel on the left and Delete on the right. */}
            {editing && confirming && mode?.kind === 'edit' && (
              <div className="space-y-2 pt-2">
                <p className="text-[0.9375rem] font-semibold text-foreground">{t('todo.own.deleteConfirm', { title: mode.target.title })}</p>
                <ActionRow onCancel={() => setConfirming(false)} disabled={deleting}>
                  <Button type="button" variant="destructive" onClick={() => { void remove(); }} disabled={deleting || own.busy} loading={deleting} busy="deleting">
                    {t('todo.own.deleteYes')}
                  </Button>
                </ActionRow>
              </div>
            )}
            {deleteFailed && <p className="text-sm text-destructive" role="alert"><FailureText text={deleteFailed} /></p>}
          </div>
        </form>
      </BottomPanel>
      {fromToday && (
        <ForPicker
          open={picking}
          onOpenChange={setPicking}
          value={forChoice}
          onPick={pickFor}
        />
      )}
    </>
  );
};
